// =============================================================================
// FIRSTLOOK.JS — Rules and labels for First Look reviews, kept out of the UI.
// =============================================================================
// The database is the authority on every rule here (see
// supabase/migrations/20261014_first_look_functions.sql). These helpers only
// decide what to SHOW: which buttons a person gets, which reviewers a dropdown
// offers, how a status reads. If one of them is wrong the server still refuses
// the action; it just makes for a worse screen. Keep the two in step.
// =============================================================================

export const REVIEWER_ROLES = {
  brief_reviewer: {
    label: 'Brief Reviewer',
    description: 'Checks campaign briefs: deliverables, deadlines, platforms, creative requirements, usage and brand guidelines.',
  },
  contract_reviewer: {
    label: 'Contract Reviewer',
    description: 'Checks contracts against the negotiated terms: fee, deliverables, usage, paid media, exclusivity, payment, ownership, termination.',
  },
}

export const DOC_TYPES = {
  contract: { label: 'Contract', role: 'contract_reviewer' },
  brief:    { label: 'Brief',    role: 'brief_reviewer' },
}

export const REVIEW_STATUS = {
  pending_review:   { label: 'Pending Review',   color: '#f59e0b' },
  in_review:        { label: 'In Review',        color: '#3b82f6' },
  changes_required: { label: 'Changes Required', color: '#ef4444' },
  approved:         { label: 'Approved',         color: '#10b981' },
}
// Not a review status: a document nobody has been asked to review yet.
export const UNASSIGNED = 'unassigned'
export const UNASSIGNED_META = { label: 'Needs Reviewer', color: '#6b7280' }

export const STATUS_ORDER = ['pending_review', 'in_review', 'changes_required', 'approved', UNASSIGNED]

export function statusMeta(status) {
  return REVIEW_STATUS[status] || UNASSIGNED_META
}

// Must match the lists in fl_add_issue.
export const ISSUE_CATEGORIES = {
  contract: [
    ['compensation', 'Compensation'],
    ['deliverables', 'Deliverables'],
    ['deadlines', 'Deadlines'],
    ['usage_rights', 'Usage rights'],
    ['paid_media', 'Paid media / boosting'],
    ['exclusivity', 'Exclusivity'],
    ['payment_terms', 'Payment terms'],
    ['ownership', 'Ownership / IP'],
    ['termination', 'Termination'],
    ['approvals', 'Approvals / revisions'],
    ['missing_protection', 'Missing protection'],
    ['other', 'Other'],
  ],
  brief: [
    ['deliverables', 'Deliverables'],
    ['deadlines', 'Deadlines'],
    ['platforms', 'Platforms'],
    ['creative_requirements', 'Creative requirements'],
    ['usage', 'Usage expectations'],
    ['brand_guidelines', 'Brand guidelines'],
    ['unclear_instructions', 'Unclear instructions'],
    ['missing_information', 'Missing information'],
    ['conflict', 'Conflict'],
    ['other', 'Other'],
  ],
}

export function categoryLabel(docType, key) {
  const hit = (ISSUE_CATEGORIES[docType] || []).find(([k]) => k === key)
    || ISSUE_CATEGORIES.contract.concat(ISSUE_CATEGORIES.brief).find(([k]) => k === key)
  return hit ? hit[1] : key
}

export const SEVERITIES = {
  high:   { label: 'High',   color: '#ef4444' },
  medium: { label: 'Medium', color: '#f59e0b' },
  low:    { label: 'Low',    color: '#6b7280' },
}

// --- files ---------------------------------------------------------------------
// Mirrors the bucket's allowed_mime_types and 25 MB limit, so a bad file is
// refused before a slow upload rather than after it.
export const MAX_FILE_BYTES = 25 * 1024 * 1024
export const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/rtf', 'text/plain',
  'image/png', 'image/jpeg', 'image/webp',
]
export const ACCEPT_ATTR = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.rtf,.txt,.png,.jpg,.jpeg,.webp'

export function validateFile(file) {
  if (!file) return 'Choose a file.'
  if (!file.size) return 'That file is empty.'
  if (file.size > MAX_FILE_BYTES) return `That file is ${formatBytes(file.size)}. The limit is 25 MB.`
  if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
    return 'Upload a PDF, Word, PowerPoint, Excel, text or image file.'
  }
  return null
}

// Storage keys must be plain: no slashes (they make folders), no characters
// that need encoding. The original name is kept separately for display.
export function safeFileName(name) {
  const base = String(name || 'document').normalize('NFKD').replace(/[̀-ͯ]/g, '')
  const cleaned = base.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^[-.]+|[-.]+$/g, '')
  return (cleaned || 'document').slice(0, 120)
}

// {org}/{random}/{name}: the first folder is what the storage policy checks.
export function buildUploadPath(orgId, randomId, fileName) {
  if (!orgId) throw new Error('Missing organisation')
  return `${orgId}/${randomId}/${safeFileName(fileName)}`
}

export function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

// --- people --------------------------------------------------------------------

/** Map of user id -> Set of reviewer roles, from first_look_reviewers rows. */
export function rolesByUser(roleRows) {
  const map = new Map()
  for (const r of roleRows || []) {
    if (!map.has(r.user_id)) map.set(r.user_id, new Set())
    map.get(r.user_id).add(r.reviewer_role)
  }
  return map
}

/** Team members who may be assigned a review of this document type. */
export function eligibleReviewers(team, roleRows, docType) {
  const role = DOC_TYPES[docType]?.role
  if (!role) return []
  const roles = rolesByUser(roleRows)
  return (team || [])
    .filter(m => roles.get(m.id)?.has(role))
    .sort((a, b) => displayName(a).localeCompare(displayName(b)))
}

export function displayName(person) {
  return person?.full_name || person?.agent_name || 'Unknown'
}

// --- what a person may do --------------------------------------------------------

/**
 * Which actions to offer on the CURRENT review of a document.
 * `review` is the current (not superseded) review or null.
 */
export function reviewActions({ review, latestVersionId, userId, isAdmin }) {
  const none = {
    canStart: false, canRequestChanges: false, canApprove: false,
    canAddIssue: false, canReassign: false, canAssign: false,
  }
  if (!review) return { ...none, canAssign: true }
  const isReviewer = !!userId && review.reviewer_id === userId
  const isRequester = !!userId && review.requested_by === userId
  const current = !review.superseded_at && review.version_id === latestVersionId
  const open = current && review.status !== 'approved'
  return {
    canStart: open && isReviewer && review.status !== 'in_review',
    canRequestChanges: open && isReviewer && review.status !== 'changes_required',
    canApprove: open && isReviewer,
    canAddIssue: open && isReviewer,
    canReassign: open && (isAdmin || isReviewer || isRequester),
    canAssign: false,
  }
}

export function canResolveIssue({ review, userId, isAdmin }) {
  if (!review || !userId) return !!isAdmin
  return isAdmin || review.reviewer_id === userId || review.requested_by === userId
}

// --- dashboard -------------------------------------------------------------------

/** Dashboard status of an overview row: its review status, or unassigned. */
export function rowStatus(row) {
  return row?.review_id ? row.review_status : UNASSIGNED
}

/** True when the latest upload is newer than the version that was approved. */
export function approvalIsStale(row) {
  return !!row?.approved_version_number && row.approved_version_number !== row.latest_version_number
}

export function filterOverview(rows, filters = {}, userId) {
  const q = (filters.search || '').trim().toLowerCase()
  return (rows || []).filter(r => {
    if (filters.mine && r.reviewer_id !== userId) return false
    if (filters.reviewer && r.reviewer_id !== filters.reviewer) return false
    if (filters.docType && r.doc_type !== filters.docType) return false
    if (filters.status && rowStatus(r) !== filters.status) return false
    if (filters.creator && !(r.creators || []).includes(filters.creator)) return false
    if (q) {
      const hay = `${r.title || ''} ${r.deal_label || ''} ${(r.creators || []).join(' ')}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
}

export function countByStatus(rows) {
  const counts = Object.fromEntries(STATUS_ORDER.map(s => [s, 0]))
  for (const r of rows || []) counts[rowStatus(r)] = (counts[rowStatus(r)] || 0) + 1
  return counts
}

// Open work first (oldest waiting at the top), finished work after.
export function sortOverview(rows) {
  const rank = { changes_required: 0, pending_review: 1, in_review: 2, unassigned: 3, approved: 4 }
  return [...(rows || [])].sort((a, b) => {
    const d = (rank[rowStatus(a)] ?? 9) - (rank[rowStatus(b)] ?? 9)
    if (d) return d
    return String(b.last_activity_at || '').localeCompare(String(a.last_activity_at || ''))
  })
}

// --- history -----------------------------------------------------------------------

export function describeEvent(ev) {
  const m = ev?.metadata || {}
  const who = ev?.actor_name || 'Someone'
  const st = s => statusMeta(s).label
  switch (ev?.event_type) {
    case 'document_created':  return `${who} created this ${m.doc_type === 'brief' ? 'brief' : 'document'}`
    case 'version_uploaded':  return `${who} uploaded version ${m.version_number}${m.file_name ? ` (${m.file_name})` : ''}`
    case 'review_requested':  return `${who} asked ${m.reviewer_name || 'a reviewer'} to review`
    case 'review_reassigned': return `${who} reassigned the review from ${m.from_reviewer_name || 'nobody'} to ${m.to_reviewer_name || 'someone'}`
    case 'review_superseded': return `Review of the previous version closed (${st(ev.from_status)}) — a new version was uploaded`
    case 'status_changed':    return `${who} changed the status: ${st(ev.from_status)} → ${st(ev.to_status)}`
    case 'comment':           return `${who} commented`
    case 'issue_added':       return `${who} logged a ${m.severity || ''} issue: ${categoryLabel(null, m.category)}`
    case 'issue_resolved':    return `${who} resolved an issue (${categoryLabel(null, m.category)})`
    case 'issue_reopened':    return `${who} reopened an issue (${categoryLabel(null, m.category)})`
    case 'marked_signed':     return `${who} recorded version ${m.version_number} as signed${m.signed_on ? ` on ${m.signed_on}` : ''}${m.internally_approved === false ? ' — this version was not internally approved' : ''}`
    case 'document_accessed': return `${who} ${m.action === 'download' ? 'downloaded' : 'opened'} version ${m.version_number}`
    default:                  return `${who}: ${ev?.event_type || 'activity'}`
  }
}

export function sortEvents(events) {
  return [...(events || [])].sort((a, b) =>
    String(a.created_at).localeCompare(String(b.created_at)) || (Number(a.seq) || 0) - (Number(b.seq) || 0))
}

export function formatWhen(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// --- AI review -----------------------------------------------------------------
// Advisory only. These describe what to show; the edge function decides nothing
// about a review's status.
export const AI_VERDICT = {
  good_to_go:             { label: 'Good to go',              color: '#10b981', icon: '✓' },
  minor_points:           { label: 'A few points to confirm', color: '#f59e0b', icon: '!' },
  address_before_signing: { label: 'Address before signing',  color: '#ef4444', icon: '✕' },
}
export const AI_CHECK_STATUS = {
  ok:             { label: 'OK',        color: '#10b981', icon: '✓' },
  attention:      { label: 'Attention', color: '#f59e0b', icon: '!' },
  missing:        { label: 'Missing',   color: '#ef4444', icon: '?' },
  not_applicable: { label: 'N/A',       color: '#6b7280', icon: '–' },
}
export const AI_READABLE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'text/plain']
export const AI_STALE_MS = 3 * 60 * 1000

export function aiCanRead(mime) {
  return AI_READABLE_TYPES.includes(mime || '')
}

/** Newest analysis of a given version, or null. */
export function latestAnalysis(analyses, versionId) {
  return (analyses || [])
    .filter(a => a.version_id === versionId)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0] || null
}

/** running | completed | failed | stale | none — "stale" is a run that never reported back. */
export function analysisState(analysis, now = Date.now()) {
  if (!analysis) return 'none'
  if (analysis.status === 'completed') return 'completed'
  if (analysis.status === 'failed') return 'failed'
  const age = now - new Date(analysis.created_at).getTime()
  return age > AI_STALE_MS ? 'stale' : 'running'
}

export function topicLabel(topic) {
  const extra = { compensation: 'Fee', platforms: 'Platforms', usage: 'Usage', creative_requirements: 'Creative requirements', brand_guidelines: 'Brand guidelines' }
  return extra[topic] || categoryLabel(null, topic)
}
